import { useLocation } from "react-router-dom";
import { CancelRequestController } from "../../util/cancel-request-controller";
import { DatosFormInstitutoAPI } from "../../services/api/LoginInstitucional";
import { useQueryParams } from "../../hooks/useQueryParams";

/**
 * Hook encargado de gestionar el envío del formulario institucional
 * basado en municipio.
 *
 * Obtiene información de la URL, construye los datos requeridos por
 * el servicio de ingreso institucional y gestiona la respuesta del API.
 *
 * Si el servicio retorna un error de validación (`status === 0`),
 * ejecuta el callback `onShowModal`. En caso contrario, redirige al
 * usuario a la URL proporcionada por el servicio.
 *
 * @param {Object} props - Configuración del hook.
 * @param {string} props.institucion - Identificador de la institución que se enviará al servicio.
 * @param {Function} props.onShowModal - Callback ejecutado cuando el servicio requiere mostrar un mensaje de alerta.
 *
 * @returns {Object} Funciones disponibles para gestionar el formulario.
 * @returns {Function} returns.submitForm - Envía los datos del formulario
 * al servicio institucional.
 */
export const useInstitutionalMunicipalitySubmit = ({
    institucion,
    onShowModal
}) => {
    const location = useLocation();
    const params = useQueryParams();

    const redirect = params.get("redirectto");

    /**
     * Envía los datos del formulario institucional al servicio de autenticación.
     *
     * Determina el tipo de ingreso según la ruta y el parámetro `access` de la URL, construye el objeto de propiedades requerido
     * por el API y procesa la respuesta del servicio.
     *
     * @param {Object} data - Datos ingresados en el formulario.
     * @param {string} [data.ciudad] - Municipio seleccionado.
     * @param {string} [data.tipoId] - Tipo de documento seleccionado.
     * @param {string} [data.documento] - Número de documento.
     * @param {string} [data.nombreYapellido] - Nombre completo del usuario.
     * @param {string} [data.emailInput] - Correo electrónico del usuario.
     *
     * @returns {Promise<void>} Promesa que finaliza cuando el envío
     * y procesamiento de la respuesta han terminado.
     *
     * @throws {Error} Propaga los errores producidos durante la solicitud
     * al servicio.
     */
    const submitForm = async (data) => {
        const abortController = new CancelRequestController();

        let tipoIngreso = "";

        if (
            location.pathname === "/institucional/formulario" &&
            params.get("access") === "app"
        ) {
            tipoIngreso = "app";
        }

        const propiedades = {
            inst: institucion || "",
            ciudad: data?.ciudad || "",
            tipoId: data?.tipoId || "",
            cedula: data?.documento || "",
            nombre: data?.nombreYapellido || "",
            correo: data?.emailInput || "",
            tipoIngreso,
            redirect
        };

        try {
            const respuesta = await DatosFormInstitutoAPI.enviarDatos({
                abortSignal: abortController.signal,
                data: propiedades
            });

            if (respuesta.status === 0) {
                onShowModal && onShowModal();
            } else {
                window.location.href =
                    window.location.origin + respuesta.info;
            }
        } finally {
            abortController.abort();
        }
    };

    return {
        submitForm
    };
};